import { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChevronDown } from "lucide-react";
import { useMakes } from "@/hooks/useMakes";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { useLanguage } from "@/context/LanguageContext";
import { carTestLabels } from "@/i18n/labels";

interface ModelSelectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedMake: string;
  selectedModel: string;
  onSelect: (model: string) => void;
  vehicleType?: "car" | "mc";
}

export const ModelSelectionDialog = ({
  open,
  onOpenChange,
  selectedMake,
  selectedModel,
  onSelect,
  vehicleType = "car",
}: ModelSelectionDialogProps) => {
  const [showAllModels, setShowAllModels] = useState(false);
  const [showModelOtherInput, setShowModelOtherInput] = useState(false);
  const [modelOtherText, setModelOtherText] = useState("");
  const { language } = useLanguage();
  const { isNative } = useNativePlatform();
  const ct = carTestLabels(language);
  const labels = ct.dialogs.verifyVehicleInfo;

  const { makes, isLoadingMakes } = useMakes(true);

  // Filter makes based on vehicle type
  const filteredMakes = useMemo(() => {
    if (!makes.length) return [];
    return makes.filter((m) => (vehicleType === "mc" ? m.isMC : !m.isMC));
  }, [makes, vehicleType]);

  // Get selected make object
  const selectedMakeObj = useMemo(() => {
    if (!selectedMake || !filteredMakes.length) return null;
    return filteredMakes.find((m) => m.name === selectedMake);
  }, [filteredMakes, selectedMake]);

  // Get models for selected make (excluding "Övrigt")
  const availableModels = useMemo(() => {
    if (!selectedMakeObj) return [];
    return selectedMakeObj.models.filter((m) => m.name !== "Övrigt");
  }, [selectedMakeObj]);

  // Display models: show 7 models + Övrigt = 8 items (2 rows of 4 columns)
  const displayedModels = useMemo(() => {
    if (!availableModels.length) return [];
    if (showAllModels) {
      return availableModels;
    }
    // Show 7 models (to fill 2 rows of 4 columns, with Övrigt as 8th item)
    return availableModels.slice(0, 7);
  }, [availableModels, showAllModels]);

  const handleModelSelect = (modelName: string) => {
    onSelect(modelName);
    onOpenChange(false);
    setShowModelOtherInput(false);
    setModelOtherText("");
  };

  const handleModelOtherClick = () => {
    setShowModelOtherInput(true);
    // If we already have a model value, pre-fill it
    if (selectedModel && !modelOtherText) {
      setModelOtherText(selectedModel);
    }
  };

  const handleModelOtherSubmit = () => {
    if (modelOtherText.trim()) {
      handleModelSelect(modelOtherText.trim());
    }
  };

  if (!selectedMake) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent nested={isNative}>
          <DialogHeader>
            <DialogTitle>{labels.selectModel}</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground text-center py-8">
            {labels.errors?.selectMakeFirst ?? "Select make first"}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        nested={isNative}
        className="max-w-2xl max-h-[80vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>
            {labels.selectModelFor.replace("{make}", selectedMake)}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {isLoadingMakes ? (
            <div className="text-sm text-muted-foreground text-center py-8">
              {labels.loadingModels}
            </div>
          ) : availableModels.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-8">
              {labels.noModelsFor.replace("{make}", selectedMake)}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {displayedModels.map((modelItem) => (
                  <Button
                    variant="formOption"
                    key={modelItem.id}
                    onClick={() => handleModelSelect(modelItem.name)}
                    className={`${
                      selectedModel === modelItem.name
                        ? "border-accent border-2"
                        : "border-gray-400"
                    } truncate`}
                    title={modelItem.name}
                  >
                    {modelItem.name}
                  </Button>
                ))}
                {/* Övrigt option */}
                {!showModelOtherInput && (
                  <Button
                    variant="formOption"
                    onClick={handleModelOtherClick}
                    className="bg-blue-200 dark:bg-blue-900/30 border-blue-400"
                  >
                    {labels.other}
                  </Button>
                )}
              </div>

              {showModelOtherInput && (
                <div className="flex gap-2">
                  <Input
                    value={modelOtherText}
                    onChange={(e) => setModelOtherText(e.target.value)}
                    placeholder={labels.enterModel}
                    maxLength={30}
                    className="flex-1"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleModelOtherSubmit();
                      }
                    }}
                  />
                  <Button
                    onClick={handleModelOtherSubmit}
                    disabled={!modelOtherText.trim()}
                  >
                    {labels.save}
                  </Button>
                </div>
              )}

              {availableModels.length > 7 && (
                <Button
                  variant="ghost"
                  className="w-full flex justify-center gap-2"
                  onClick={() => setShowAllModels(!showAllModels)}
                >
                  {showAllModels
                    ? (labels.showLessModels ?? labels.showLess)
                    : (labels.showMoreModels ?? labels.showMore)}
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${
                      showAllModels ? "rotate-180" : ""
                    }`}
                  />
                </Button>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
